import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { MultiSelect } from "../components/ui/multi-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Plus, Trash2, Copy, 
  FileSpreadsheet, Wallet, Filter,
  Building2, Settings, Eye, GitCompare, RefreshCw, Save, Layers,
  CheckCircle, AlertTriangle, XCircle
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line
} from "recharts";
import React from "react";
import { BudgetKPICards, BudgetConfrontoTable, BudgetPreventivoTable } from "../components/budget";
import { useTranslation } from "../contexts/I18nContext";

const MESI_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export default function BudgetPage() {
  const { t } = useTranslation();
  const MESI_FULL = [t("months.january"), t("months.february"), t("months.march"), t("months.april"), t("months.may"), t("months.june"), t("months.july"), t("months.august"), t("months.september"), t("months.october"), t("months.november"), t("months.december")];
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState("economico");
  const [viewMode, setViewMode] = useState("preventivo");
  
  // Data states
  const [budgetEntries, setBudgetEntries] = useState([]);
  const [consuntivoData, setConsuntivoData] = useState(null);
  const [conti, setConti] = useState([]);
  const [centriCosto, setCentriCosto] = useState([]);
  const [categorieConti, setCategorieConti] = useState([]);
  const [budgetCategorie, setBudgetCategorie] = useState([]);
  
  // UI states
  const [expandedCategories, setExpandedCategories] = useState({});
  const [filtroContoId, setFiltroContoId] = useState("all");
  const [filtroCdcIds, setFiltroCdcIds] = useState([]); // Multi-select CDC
  const [zoomedMonth, setZoomedMonth] = useState(null); // null = mostra tutti i mesi, 0-11 = zoom su singolo mese
  const [isTableExpanded, setIsTableExpanded] = useState(false); // Toggle per espandere tabella
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [categorieDialogOpen, setCategorieDialogOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [cellValue, setCellValue] = useState("");
  
  const [form, setForm] = useState({
    conto_id: "",
    centro_costo_ids: [], // Multi-select CDC
    categoria_id: "none",
    tipo: "costo",
    importo_preventivo: "",
    mese: 1,
    note: ""
  });
  
  const [copyForm, setCopyForm] = useState({
    anno_destinazione: new Date().getFullYear() + 1,
    percentuale_variazione: 0
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [entriesRes, contiRes, centriRes, catRes] = await Promise.all([
        api.get(`${prefix}/budget/entries?anno=${anno}`),
        api.get(`${prefix}/accounts/with-categories`),
        api.get(`${prefix}/cost-centers`),
        api.get(`${prefix}/account-categories`)
      ]);
      setBudgetEntries(entriesRes.data);
      setConti(contiRes.data);
      setCentriCosto(centriRes.data);
      setCategorieConti(catRes.data || []);
      buildCategoriesFromAccounts(contiRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [anno, t]);

  useEffect(() => {
    const loadConsuntivo = async () => {
      if (viewMode === "consuntivo" || viewMode === "confronto") {
        setLoading(true);
        try {
          const prefix = getApiPrefix();
          const res = await api.get(`${prefix}/reports/bilancio-competenza?anno=${anno}`);
          setConsuntivoData(res.data);
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.error("Errore caricamento consuntivo:", err);
        } finally {
          setLoading(false);
        }
      }
    };
    loadConsuntivo();
  }, [viewMode, anno]);

  const buildCategoriesFromAccounts = (accountsData) => {
    // Build categories from backend auto-categorization (same logic as bilancio competenza)
    const catMap = {};
    (accountsData || []).forEach(acc => {
      const catId = acc.auto_categoria_id;
      if (!catId) return;
      if (!catMap[catId]) {
        const codice = acc.auto_categoria_codice || '';
        const isRicavo = codice.startsWith('5');
        catMap[catId] = {
          id: catId,
          label: acc.auto_categoria_nome || `Conti ${codice}`,
          tipo: isRicavo ? "ricavo" : "costo",
          codice_prefix: codice,
          colore: isRicavo ? "bg-green-50" : "bg-red-50",
          ordine: acc.auto_categoria_ordine || (isRicavo ? 0 : 100),
          categoria_id: catId
        };
      }
    });

    const categorie = Object.values(catMap);
    if (!categorie.some(c => c.tipo === "ricavo")) {
      categorie.push({ id: "ricavi_default", label: "Ricavi", tipo: "ricavo", codice_prefix: "5", colore: "bg-green-50", ordine: 0 });
    }
    if (!categorie.some(c => c.tipo === "costo")) {
      categorie.push({ id: "costi_default", label: "Costi", tipo: "costo", codice_prefix: "7", colore: "bg-red-50", ordine: 100 });
    }
    categorie.push({ id: "cassa_entrate", label: "Entrate di Cassa", tipo: "cassa_entrata", colore: "bg-blue-50", ordine: 200 });
    categorie.push({ id: "cassa_uscite", label: "Uscite di Cassa", tipo: "cassa_uscita", colore: "bg-orange-50", ordine: 201 });
    setBudgetCategorie(categorie.sort((a, b) => a.ordine - b.ordine));
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build budget matrix - raggruppato per conto (una riga per conto)
  const budgetMatrix = useMemo(() => {
    const categorie = budgetCategorie.filter(c => {
      if (activeTab === "economico") return c.tipo === "ricavo" || c.tipo === "costo";
      return c.tipo === "cassa_entrata" || c.tipo === "cassa_uscita";
    });
    
    const matrix = {};
    categorie.forEach(cat => {
      matrix[cat.id] = {
        ...cat,
        mesi: Array(12).fill(0),
        totale: 0,
        conti: {} // Raggruppato per conto_id
      };
    });
    
    // Applica filtri CDC (multi-select)
    const filteredEntries = budgetEntries
      .filter(e => filtroContoId === "all" || e.conto_id === filtroContoId)
      .filter(e => filtroCdcIds.length === 0 || filtroCdcIds.includes(e.centro_costo_id));
    
    filteredEntries.forEach(entry => {
      // Use backend auto-categorization from conti (same logic as bilancio competenza)
      let categoriaId = null;
      const conto = conti.find(c => c.id === entry.conto_id);
      if (conto?.auto_categoria_id) {
        categoriaId = conto.auto_categoria_id;
      }
      // Fallback per tipo cassa
      if (!categoriaId || !matrix[categoriaId]) {
        if (entry.tipo === "cassa_entrata") categoriaId = "cassa_entrate";
        else if (entry.tipo === "cassa_uscita") categoriaId = "cassa_uscite";
        else {
          const codice = String(entry.conto_codice || conto?.codice || '');
          categoriaId = codice.startsWith('5') ? "ricavi_default" : "costi_default";
        }
      }
      
      if (categoriaId && matrix[categoriaId]) {
        const importo = parseFloat(entry.importo_preventivo) || 0;
        const mese = (entry.mese || 1) - 1;
        
        if (mese >= 0 && mese < 12) {
          matrix[categoriaId].mesi[mese] += importo;
          matrix[categoriaId].totale += importo;
          
          // Raggruppa per conto
          const contoKey = entry.conto_id || "no_conto";
          if (!matrix[categoriaId].conti[contoKey]) {
            matrix[categoriaId].conti[contoKey] = {
              conto_id: entry.conto_id,
              conto_codice: entry.conto_codice || "",
              conto_nome: entry.conto_nome || "Non assegnato",
              mesi: Array(12).fill(0),
              totale: 0,
              entries: []
            };
          }
          matrix[categoriaId].conti[contoKey].mesi[mese] += importo;
          matrix[categoriaId].conti[contoKey].totale += importo;
          matrix[categoriaId].conti[contoKey].entries.push({ ...entry, mese });
        }
      }
    });
    
    return matrix;
  }, [budgetEntries, budgetCategorie, activeTab, filtroContoId, filtroCdcIds, conti]);

  // Build consuntivo matrix from bilancio data
  // Uses backend auto-categorization directly from per_conto (same as bilancio competenza)
  const consuntivoMatrix = useMemo(() => {
    if (!consuntivoData) return {};

    const matrix = {};

    // Helper: ensure category exists in matrix
    const ensureCategory = (catId, catNome, tipo, ordine) => {
      if (!matrix[catId]) {
        const codice = catId.replace('auto_', '');
        matrix[catId] = {
          id: catId,
          label: catNome || catId,
          tipo,
          codice_prefix: codice,
          colore: tipo === "ricavo" ? "bg-green-50" : tipo === "costo" ? "bg-red-50" : tipo === "cassa_entrata" ? "bg-blue-50" : "bg-orange-50",
          ordine: ordine || 999,
          mesi: Array(12).fill(0),
          totale: 0,
          conti: {}
        };
      }
    };

    // Also pre-populate from budgetCategorie to maintain consistency
    budgetCategorie.forEach(cat => {
      if (activeTab === "economico" && (cat.tipo === "ricavo" || cat.tipo === "costo")) {
        ensureCategory(cat.id, cat.label, cat.tipo, cat.ordine);
      } else if (activeTab !== "economico" && (cat.tipo === "cassa_entrata" || cat.tipo === "cassa_uscita")) {
        ensureCategory(cat.id, cat.label, cat.tipo, cat.ordine);
      }
    });

    if (activeTab === "economico") {
      // Process ricavi from per_conto (backend already has category info)
      (consuntivoData.ricavi?.per_conto || []).forEach(conto => {
        const catId = conto.categoria_id || "ricavi_default";
        const catNome = conto.categoria_nome || "Ricavi";
        const catOrdine = conto.categoria_ordine || 0;
        ensureCategory(catId, catNome, "ricavo", catOrdine);

        const contoKey = conto.conto_id || "no_conto";
        if (!matrix[catId].conti[contoKey]) {
          matrix[catId].conti[contoKey] = {
            conto_id: conto.conto_id,
            conto_codice: conto.conto_codice || "",
            conto_nome: conto.conto_nome || "N/D",
            mesi: Array(12).fill(0),
            totale: 0
          };
        }

        let contoTotale = 0;
        (conto.dettaglio || []).forEach(item => {
          if (filtroCdcIds.length > 0 && !filtroCdcIds.includes(item.centro_costo_id)) return;
          const importo = parseFloat(item.importo) || 0;
          const dataInizio = item.data_inizio;
          if (dataInizio) {
            const mese = parseInt(dataInizio.split('-')[1]) - 1;
            if (mese >= 0 && mese < 12) {
              matrix[catId].mesi[mese] += importo;
              matrix[catId].conti[contoKey].mesi[mese] += importo;
            }
          }
          contoTotale += importo;
        });
        matrix[catId].totale += contoTotale;
        matrix[catId].conti[contoKey].totale += contoTotale;
      });

      // Process costi from per_conto
      (consuntivoData.costi?.per_conto || []).forEach(conto => {
        const catId = conto.categoria_id || "costi_default";
        const catNome = conto.categoria_nome || "Costi";
        const catOrdine = conto.categoria_ordine || 100;
        ensureCategory(catId, catNome, "costo", catOrdine);

        const contoKey = conto.conto_id || "no_conto";
        if (!matrix[catId].conti[contoKey]) {
          matrix[catId].conti[contoKey] = {
            conto_id: conto.conto_id,
            conto_codice: conto.conto_codice || "",
            conto_nome: conto.conto_nome || "N/D",
            mesi: Array(12).fill(0),
            totale: 0
          };
        }

        let contoTotale = 0;
        (conto.dettaglio || []).forEach(item => {
          if (filtroCdcIds.length > 0 && !filtroCdcIds.includes(item.centro_costo_id)) return;
          const importo = parseFloat(item.importo) || 0;
          const dataInizio = item.data_inizio;
          if (dataInizio) {
            const mese = parseInt(dataInizio.split('-')[1]) - 1;
            if (mese >= 0 && mese < 12) {
              matrix[catId].mesi[mese] += importo;
              matrix[catId].conti[contoKey].mesi[mese] += importo;
            }
          }
          contoTotale += importo;
        });
        matrix[catId].totale += contoTotale;
        matrix[catId].conti[contoKey].totale += contoTotale;
      });
    } else {
      // Cassa
      ensureCategory("cassa_entrate", "Entrate di Cassa", "cassa_entrata", 200);
      ensureCategory("cassa_uscite", "Uscite di Cassa", "cassa_uscita", 201);
      (consuntivoData.ricavi?.per_conto || []).forEach(conto => {
        (conto.dettaglio || []).forEach(item => {
          if (filtroCdcIds.length > 0 && !filtroCdcIds.includes(item.centro_costo_id)) return;
          const importo = parseFloat(item.importo) || 0;
          const dataInizio = item.data_inizio;
          if (dataInizio) {
            const mese = parseInt(dataInizio.split('-')[1]) - 1;
            if (mese >= 0 && mese < 12) matrix["cassa_entrate"].mesi[mese] += importo;
          }
        });
      });
      matrix["cassa_entrate"].totale = matrix["cassa_entrate"].mesi.reduce((s, v) => s + v, 0);
      (consuntivoData.costi?.per_conto || []).forEach(conto => {
        (conto.dettaglio || []).forEach(item => {
          if (filtroCdcIds.length > 0 && !filtroCdcIds.includes(item.centro_costo_id)) return;
          const importo = parseFloat(item.importo) || 0;
          const dataInizio = item.data_inizio;
          if (dataInizio) {
            const mese = parseInt(dataInizio.split('-')[1]) - 1;
            if (mese >= 0 && mese < 12) matrix["cassa_uscite"].mesi[mese] += importo;
          }
        });
      });
      matrix["cassa_uscite"].totale = matrix["cassa_uscite"].mesi.reduce((s, v) => s + v, 0);
    }

    return matrix;
  }, [consuntivoData, budgetCategorie, activeTab, filtroCdcIds]);

  // Calculate totals
  const totali = useMemo(() => {
    const dataMatrix = viewMode === "consuntivo" ? consuntivoMatrix : budgetMatrix;
    // For consuntivo, categories are built dynamically in the matrix
    // For preventivo, use budgetCategorie
    const allCatIds = Object.keys(dataMatrix);

    const tipoPositivo = activeTab === "economico" ? "ricavo" : "cassa_entrata";

    const totaliMensili = Array(12).fill(0).map((_, mese) => {
      let entrate = 0, uscite = 0;
      allCatIds.forEach(catId => {
        const cat = dataMatrix[catId];
        if (!cat) return;
        // Filter by active tab
        if (activeTab === "economico" && cat.tipo !== "ricavo" && cat.tipo !== "costo") return;
        if (activeTab !== "economico" && cat.tipo !== "cassa_entrata" && cat.tipo !== "cassa_uscita") return;
        const val = cat.mesi?.[mese] || 0;
        if (cat.tipo === tipoPositivo) entrate += val;
        else uscite += val;
      });
      return { entrate, uscite, saldo: entrate - uscite };
    });

    return {
      mensili: totaliMensili,
      entrate: totaliMensili.reduce((s, m) => s + m.entrate, 0),
      uscite: totaliMensili.reduce((s, m) => s + m.uscite, 0),
      saldo: totaliMensili.reduce((s, m) => s + m.saldo, 0)
    };
  }, [budgetMatrix, consuntivoMatrix, activeTab, viewMode]);

  // Handlers
  const handleSave = async () => {
    if (!form.importo_preventivo) {
      handleApiError({ message: t("errors.requiredAmountEnter") });
      return;
    }
    try {
      const prefix = getApiPrefix();
      // Se multi-CDC, crea entry per ogni CDC
      const cdcList = form.centro_costo_ids.length > 0 ? form.centro_costo_ids : [null];
      for (const cdcId of cdcList) {
        await api.post(`${prefix}/budget/entries`, {
          anno: anno,
          mese: form.mese,
          tipo: form.tipo,
          conto_id: form.conto_id || null,
          centro_costo_id: cdcId,
          categoria_id: form.categoria_id === "none" ? null : form.categoria_id,
          importo_preventivo: parseFloat(form.importo_preventivo) / cdcList.length
        });
      }
      showSuccess(t("success.budgetSaved"));
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleSaveCell = async (contoId, mese, categoriaId, tipo) => {
    if (!cellValue) return;
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/budget/entries`, {
        anno: anno,
        mese: mese + 1,
        tipo: tipo,
        conto_id: contoId || null,
        centro_costo_id: null,
        categoria_id: categoriaId === "none" ? null : categoriaId,
        importo_preventivo: parseFloat(cellValue)
      });
      showSuccess(t("success.saved"));
      setEditingCell(null);
      setCellValue("");
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteBudgetItem"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/budget/entries/${id}`);
      showSuccess(t("success.costDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleCopy = async () => {
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/budget/copia?anno_origine=${anno}&anno_destinazione=${copyForm.anno_destinazione}&percentuale_variazione=${copyForm.percentuale_variazione}`);
      showSuccess(`Budget copiato in ${copyForm.anno_destinazione}`);
      setCopyDialogOpen(false);
    } catch (err) {
      handleApiError(err, t("errors.errorCopyBudget"));
    }
  };

  const resetForm = () => {
    setForm({
      conto_id: "",
      centro_costo_ids: [],
      categoria_id: "none",
      tipo: activeTab === "economico" ? "costo" : "cassa_uscita",
      importo_preventivo: "",
      mese: 1,
      note: ""
    });
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null || isNaN(val)) return "€ 0";
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
  };

  const getStatoBadge = (varianzaPerc) => {
    const perc = Math.abs(varianzaPerc);
    if (perc <= 5) return <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle className="h-3 w-3" /></Badge>;
    if (perc <= 10) return <Badge className="bg-yellow-100 text-yellow-800 text-xs"><AlertTriangle className="h-3 w-3" /></Badge>;
    return <Badge className="bg-red-100 text-red-800 text-xs"><XCircle className="h-3 w-3" /></Badge>;
  };

  if (loading && viewMode === "preventivo") return <PageLoader />;

  // For consuntivo, categories are dynamic in the matrix; for preventivo, from budgetCategorie
  const categorie = viewMode === "consuntivo"
    ? Object.values(consuntivoMatrix)
        .filter(c => activeTab === "economico" ? (c.tipo === "ricavo" || c.tipo === "costo") : (c.tipo === "cassa_entrata" || c.tipo === "cassa_uscita"))
        .sort((a, b) => (a.ordine || 999) - (b.ordine || 999))
    : budgetCategorie.filter(c => {
        if (activeTab === "economico") return c.tipo === "ricavo" || c.tipo === "costo";
        return c.tipo === "cassa_entrata" || c.tipo === "cassa_uscita";
      });
  const tipoPositivo = activeTab === "economico" ? "ricavo" : "cassa_entrata";
  const dataMatrix = viewMode === "consuntivo" ? consuntivoMatrix : budgetMatrix;

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="budget-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            {viewMode === "preventivo" && t("budget.budgetForecast")}
            {viewMode === "consuntivo" && "Consuntivo"}
            {viewMode === "confronto" && "Confronto Budget vs Consuntivo"}
            {" "}{anno}
          </h1>
          <p className="text-muted-foreground text-sm">
            {viewMode === "preventivo" && "Pianificazione economica e finanziaria"}
            {viewMode === "consuntivo" && "Dati effettivi dal bilancio di competenza"}
            {viewMode === "confronto" && "Analisi varianze tra preventivo e consuntivo"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {viewMode === "preventivo" && (
            <>
              <Button variant="outline" size="sm" onClick={() => setCategorieDialogOpen(true)}>
                <Settings className="h-4 w-4 mr-1" />{t('common.categories')}</Button>
              <Button variant="outline" size="sm" onClick={() => setCopyDialogOpen(true)}>
                <Copy className="h-4 w-4 mr-1" />
                Copia Anno
              </Button>
              <Button size="sm" onClick={() => { resetForm(); setDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-1" />
                Nuova Voce
              </Button>
            </>
          )}
          {(viewMode === "consuntivo" || viewMode === "confronto") && (
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); setTimeout(() => setLoading(false), 100); }}>
              <RefreshCw className="h-4 w-4 mr-1" />{t('common.refresh')}</Button>
          )}
        </div>
      </div>

      {/* View Mode Selector */}
      <div className="flex gap-2 p-1 bg-muted rounded-lg w-fit">
        <Button variant={viewMode === "preventivo" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("preventivo")} className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" />Preventivo
        </Button>
        <Button variant={viewMode === "consuntivo" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("consuntivo")} className="flex items-center gap-2">
          <Eye className="h-4 w-4" />Consuntivo
        </Button>
        <Button variant={viewMode === "confronto" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("confronto")} className="flex items-center gap-2">
          <GitCompare className="h-4 w-4" />Confronto
        </Button>
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full lg:w-auto">
          <TabsList className="grid grid-cols-2 w-full lg:w-[400px]">
            <TabsTrigger value="economico" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />Conto Economico
            </TabsTrigger>
            <TabsTrigger value="cassa" className="flex items-center gap-2">
              <Wallet className="h-4 w-4" />Flussi di Cassa
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex gap-2 flex-wrap items-center">
          <Select value={String(anno)} onValueChange={(v) => setAnno(parseInt(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2023, 2024, 2025, 2026, 2027, 2028].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {(viewMode === "preventivo" || viewMode === "consuntivo") && (
            <>
              {viewMode === "preventivo" && (
                <Select value={filtroContoId} onValueChange={setFiltroContoId}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Tutti i conti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i conti</SelectItem>
                    {conti.map(c => (<SelectItem key={c.id} value={c.id}>{c.codice} - {c.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              
              <MultiSelect
                options={centriCosto.map(c => ({ value: c.id, label: c.nome }))}
                selected={filtroCdcIds}
                onChange={setFiltroCdcIds}
                placeholder="Filtra CDC..."
                searchPlaceholder={t("common.searchPlaceholder")}
                className="w-[200px]"
              />
            </>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <BudgetKPICards totali={totali} activeTab={activeTab} />

      {/* Confronto Chart */}
      {viewMode === "confronto" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Andamento Mensile - Budget vs Consuntivo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={totali.mensili.map((m, i) => {
                  const consMese = (categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.mesi[i] || 0), 0)) -
                                   (categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.mesi[i] || 0), 0));
                  return { mese: MESI_LABELS[i], budget: m.saldo, consuntivo: consMese, varianza: consMese - m.saldo };
                })}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mese" />
                  <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="budget" name="Budget" fill="#94a3b8" opacity={0.5} />
                  <Bar dataKey="consuntivo" name="Consuntivo" fill="#3b82f6" />
                  <Line type="monotone" dataKey="varianza" name="Varianza" stroke="#ef4444" strokeWidth={2} dot />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Table */}
      {viewMode === "confronto" ? (
        <BudgetConfrontoTable
          categorie={categorie}
          budgetMatrix={budgetMatrix}
          consuntivoMatrix={consuntivoMatrix}
          totali={totali}
          activeTab={activeTab}
          expandedCategories={expandedCategories}
          toggleCategory={toggleCategory}
          centriCosto={centriCosto}
          filtroCdcIds={filtroCdcIds}
          setFiltroCdcIds={setFiltroCdcIds}
          zoomedMonth={zoomedMonth}
          setZoomedMonth={setZoomedMonth}
          isTableExpanded={isTableExpanded}
          setIsTableExpanded={setIsTableExpanded}
        />
      ) : (
        <BudgetPreventivoTable
          viewMode={viewMode}
          activeTab={activeTab}
          categorie={categorie}
          dataMatrix={dataMatrix}
          totali={totali}
          expandedCategories={expandedCategories}
          toggleCategory={toggleCategory}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          cellValue={cellValue}
          setCellValue={setCellValue}
          handleSaveCell={handleSaveCell}
          handleDelete={handleDelete}
        />
      )}

      {/* Dialog Nuova Voce */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("budget.newItem")}</DialogTitle>
            <DialogDescription>Inserisci una nuova voce nel budget {activeTab === "economico" ? "economico" : "di cassa"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.type')} *</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {activeTab === "economico" ? (
                      <><SelectItem value="ricavo">{t('common.revenue')}</SelectItem><SelectItem value="costo">{t('common.cost')}</SelectItem></>
                    ) : (
                      <><SelectItem value="cassa_entrata">Entrata Cassa</SelectItem><SelectItem value="cassa_uscita">Uscita Cassa</SelectItem></>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mese *</Label>
                <Select value={String(form.mese)} onValueChange={(v) => setForm({ ...form, mese: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MESI_FULL.map((m, i) => (<SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Categoria Budget</Label>
              <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
                <SelectTrigger><SelectValue placeholder={t("common.selectPlaceholder")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Nessuna categoria --</SelectItem>
                  {budgetCategorie.filter(c => {
                    if (form.tipo === "ricavo") return c.tipo === "ricavo";
                    if (form.tipo === "costo") return c.tipo === "costo";
                    if (form.tipo === "cassa_entrata") return c.tipo === "cassa_entrata";
                    if (form.tipo === "cassa_uscita") return c.tipo === "cassa_uscita";
                    return true;
                  }).map(c => (<SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Conto (opzionale)</Label>
              <SearchableSelect
                options={[{ value: "", label: "-- Nessun conto --" }, ...conti.map(c => ({ value: c.id, label: `${c.codice} - ${c.nome}` }))]}
                value={form.conto_id}
                onValueChange={(v) => setForm({ ...form, conto_id: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder={t("common.searchPlaceholder")}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Centri di Costo (selezione multipla)</Label>
              <MultiSelect
                options={centriCosto.map(c => ({ value: c.id, label: c.nome }))}
                selected={form.centro_costo_ids}
                onChange={(v) => setForm({ ...form, centro_costo_ids: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder={t("common.searchPlaceholder")}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Importo Preventivo *</Label>
              <Input type="number" step="0.01" value={form.importo_preventivo}
                onChange={(e) => setForm({ ...form, importo_preventivo: e.target.value })} placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("common.notes")} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave}><Save className="h-4 w-4 mr-2" />{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Copia Budget */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copia Budget {anno}</DialogTitle>
            <DialogDescription>Copia tutte le voci in un altro anno con variazione percentuale</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Anno Destinazione</Label>
              <Select value={String(copyForm.anno_destinazione)} onValueChange={(v) => setCopyForm({ ...copyForm, anno_destinazione: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Variazione % (es: 5 per +5%, -10 per -10%)</Label>
              <Input type="number" step="0.1" value={copyForm.percentuale_variazione}
                onChange={(e) => setCopyForm({ ...copyForm, percentuale_variazione: parseFloat(e.target.value) || 0 })} placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCopy}><Copy className="h-4 w-4 mr-2" />Copia Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Categorie Budget */}
      <Dialog open={categorieDialogOpen} onOpenChange={setCategorieDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Categorie Budget</DialogTitle>
            <DialogDescription>
              Le categorie budget sono derivate dalle categorie conti dell'anagrafica. 
              Per modificarle, vai in Contabilità → Anagrafiche → Conti e modifica le categorie.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.category")}</TableHead>
                    <TableHead>{t("common.type")}</TableHead>
                    <TableHead>Prefisso Codice</TableHead>
                    <TableHead>Ordine</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgetCategorie.map(cat => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium">{cat.label}</TableCell>
                      <TableCell>
                        <Badge variant={cat.tipo === "ricavo" || cat.tipo === "cassa_entrata" ? "default" : "destructive"}>{cat.tipo}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{cat.codice_prefix || "-"}</TableCell>
                      <TableCell>{cat.ordine}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
              <p><strong>Come funziona:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>I conti con codice che inizia con <strong>5</strong> sono ricavi (CE)</li>
                <li>I conti con codice che inizia con <strong>7</strong> sono costi (CE)</li>
                <li>Ogni conto può avere una categoria assegnata nell'anagrafica</li>
                <li>Le voci budget vengono raggruppate per categoria del conto</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCategorieDialogOpen(false)}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
